/**
 * WM v1 Status Transition Engine
 *
 * Statuses use DB format: checked-in, checked-out, unbedded, not_present, reserved, waitlist
 * Role restrictions: staff can only set checked-in, checked-out, not_present
 *
 * NOTE: 'hospital' was removed — Cimfoni captures no PHI / medical status.
 */

export type ClientStatus = 'checked-in' | 'checked-out' | 'unbedded' | 'not_present' | 'reserved' | 'waitlist';

// Normalize any format to DB-compatible hyphenated format
export const normalizeStatus = (status: string): ClientStatus => {
  const map: Record<string, ClientStatus> = {
    'checked_in': 'checked-in',
    'checked_out': 'checked-out',
    'checked-in': 'checked-in',
    'checked-out': 'checked-out',
    'unbedded': 'unbedded',
    'not_present': 'not_present',
    'reserved': 'reserved',
    'waitlist': 'waitlist',
    // Legacy 'hospital' rows map to not_present
    'hospital': 'not_present',
  };
  return (map[status] || status) as ClientStatus;
};

export const displayStatus = (status: ClientStatus): string => {
  const map: Record<ClientStatus, string> = {
    'checked-in': 'Checked In',
    'checked-out': 'Checked Out',
    'unbedded': 'Unbedded',
    'not_present': 'Not Present',
    'reserved': 'Reserved',
    'waitlist': 'Wait List',
  };
  return map[status] || status;
};

export type StatusKey = ClientStatus | 'pass';

const STATUS_SOLID: Record<StatusKey, string> = {
  'checked-in':  'bg-[hsl(var(--status-checked-in))] text-[hsl(var(--status-checked-in-foreground))]',
  'checked-out': 'bg-[hsl(var(--status-checked-out))] text-[hsl(var(--status-checked-out-foreground))]',
  'pass':        'bg-[hsl(var(--status-pass))] text-[hsl(var(--status-pass-foreground))]',
  'unbedded':    'bg-[hsl(var(--status-unbedded))] text-[hsl(var(--status-unbedded-foreground))]',
  'not_present': 'bg-[hsl(var(--status-not-present))] text-[hsl(var(--status-not-present-foreground))]',
  'reserved':    'bg-[hsl(var(--status-reserved))] text-[hsl(var(--status-reserved-foreground))]',
  'waitlist':    'bg-[hsl(var(--status-waitlist))] text-[hsl(var(--status-waitlist-foreground))]',
};

const STATUS_SOFT: Record<StatusKey, string> = {
  'checked-in':  'bg-[hsl(var(--status-checked-in)/0.15)] text-[hsl(var(--status-checked-in))] border-[hsl(var(--status-checked-in)/0.3)]',
  'checked-out': 'bg-[hsl(var(--status-checked-out)/0.15)] text-[hsl(var(--status-checked-out))] border-[hsl(var(--status-checked-out)/0.3)]',
  'pass':        'bg-[hsl(var(--status-pass)/0.15)] text-[hsl(var(--status-pass))] border-[hsl(var(--status-pass)/0.3)]',
  'unbedded':    'bg-[hsl(var(--status-unbedded)/0.15)] text-[hsl(var(--status-unbedded))] border-[hsl(var(--status-unbedded)/0.3)]',
  'not_present': 'bg-[hsl(var(--status-not-present)/0.15)] text-[hsl(var(--status-not-present))] border-[hsl(var(--status-not-present)/0.3)]',
  'reserved':    'bg-[hsl(var(--status-reserved)/0.15)] text-[hsl(var(--status-reserved))] border-[hsl(var(--status-reserved)/0.3)]',
  'waitlist':    'bg-[hsl(var(--status-waitlist)/0.15)] text-[hsl(var(--status-waitlist))] border-[hsl(var(--status-waitlist)/0.3)]',
};

export const getStatusColor = (
  status: StatusKey,
  variant: 'solid' | 'soft' = 'soft'
): string => {
  const key = (STATUS_SOLID[status] ? status : 'checked-in') as StatusKey;
  return variant === 'solid' ? STATUS_SOLID[key] : STATUS_SOFT[key];
};

export const resolveStatusKey = (client: {
  status?: string;
  passStart?: string | null;
  passEnd?: string | null;
  pass_start?: string | null;
  pass_end?: string | null;
}): StatusKey => {
  const start = client.passStart ?? client.pass_start ?? null;
  const end = client.passEnd ?? client.pass_end ?? null;
  if (start && end) {
    const now = new Date();
    if (now >= new Date(start) && now <= new Date(end)) return 'pass';
  }
  return normalizeStatus(client.status || 'checked-out') as StatusKey;
};

export const getStatusLabel = (status: StatusKey): string => {
  if (status === 'pass') return 'Pass';
  return displayStatus(status as ClientStatus);
};

const TRANSITIONS: Record<ClientStatus, ClientStatus[]> = {
  'checked-out': ['checked-in', 'not_present', 'reserved', 'waitlist'],
  'checked-in': ['checked-out', 'unbedded'],
  'not_present': ['checked-in', 'checked-out', 'unbedded'],
  'reserved': ['checked-in', 'checked-out', 'waitlist'],
  'waitlist': ['reserved', 'checked-in'],
  'unbedded': ['waitlist', 'reserved', 'checked-in'],
};

const STAFF_ALLOWED_TARGETS: ClientStatus[] = ['checked-in', 'checked-out', 'not_present'];

export const isValidTransition = (from: ClientStatus, to: ClientStatus): boolean => {
  if (from === to) return false;
  const allowed = TRANSITIONS[from];
  return allowed ? allowed.includes(to) : false;
};

export const canRoleSetStatus = (role: 'staff' | 'admin', target: ClientStatus): boolean => {
  if (role === 'admin') return true;
  return STAFF_ALLOWED_TARGETS.includes(target);
};

export const getAvailableTransitions = (
  currentStatus: ClientStatus,
  role: 'staff' | 'admin'
): ClientStatus[] => {
  const allAllowed = TRANSITIONS[currentStatus] || [];
  if (role === 'admin') return allAllowed;
  return allAllowed.filter(s => STAFF_ALLOWED_TARGETS.includes(s));
};

export const getTransitionError = (from: ClientStatus, to: ClientStatus, role: 'staff' | 'admin'): string | null => {
  if (from === to) return `Client is already ${displayStatus(to)}`;
  if (!isValidTransition(from, to)) return `Cannot transition from ${displayStatus(from)} to ${displayStatus(to)}`;
  if (!canRoleSetStatus(role, to)) return `Only administrators can set status to ${displayStatus(to)}`;
  return null;
};

export const requiresRoom = (target: ClientStatus): boolean => target === 'checked-in';
export const requiresAdditionalInfo = (target: ClientStatus): boolean => target === 'unbedded';

export const isClientOnPass = (client: { passStart?: string; passEnd?: string }): boolean => {
  if (!client.passStart || !client.passEnd) return false;
  const now = new Date();
  return now >= new Date(client.passStart) && now <= new Date(client.passEnd);
};

export const getUnbedGuardError = (passEnd?: string | null): string | null => {
  if (!passEnd) return null;
  const end = new Date(passEnd);
  if (end > new Date()) {
    const formatted = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    return `Cannot unbed: client has an active pass until ${formatted}`;
  }
  return null;
};

export const getTimestampUpdates = (newStatus: ClientStatus): Record<string, string | null> => {
  const now = new Date().toISOString();
  const updates: Record<string, string | null> = {};
  switch (newStatus) {
    case 'checked-in':
      updates.check_in_time = now;
      updates.check_out_time = null;
      updates.pass_start = null;
      updates.pass_end = null;
      updates.pass_type = null;
      break;
    case 'checked-out': updates.check_out_time = now; break;
    case 'not_present': updates.not_present_at = now; break;
    case 'unbedded':    updates.unbedded_at = now; break;
    case 'reserved':    updates.reserved_at = now; break;
    case 'waitlist':    updates.waitlist_at = now; break;
  }
  return updates;
};
